/* SalesPipeline — drag-and-drop Kanban board of all prospects.
 *
 * iter98.52 — 7-column kanban using @hello-pangea/dnd. Dragging a
 * card between columns triggers an optimistic UI update + a
 * PATCH /api/sales/prospects/{id} call with the new `pipeline_stage`.
 * The server auto-logs a `stage_change` activity on the prospect.
 *
 * Columns: Prospect · Discovery · Lab Demo · Case Trial · Negotiation ·
 *          Contract · Active
 *
 * Card content adapts to stage — early stages show scanner / provider,
 * trial shows pilot metrics, contract+active show case volume + revenue.
 *
 * Filters at top: rep (manager only) + tier + scanner.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    Buildings, MapPin, Phone, ChartLine, Star, Stack, Coins,
    Funnel, X, Plus, ArrowSquareOut,
} from "@phosphor-icons/react";


const STAGES = [
    { id: "prospect",    label: "Prospect",   color: "#94A3B8" },
    { id: "discovery",   label: "Discovery",  color: "#0EA5E9" },
    { id: "demo",        label: "Lab Demo",   color: "#6366F1" },
    { id: "trial",       label: "Case Trial", color: "#F59E0B" },
    { id: "negotiation", label: "Negotiation",color: "#EC4899" },
    { id: "contract",    label: "Contract",   color: "#10B981" },
    { id: "active",      label: "Active",     color: "#059669" },
];

const TIER_LABEL = {
    startup:      "Startup",
    growth:       "Growth",
    established:  "Established",
    multi_clinic: "Multi-clinic",
};
const TIER_TINT = {
    startup:      "bg-slate-50 text-slate-700 ring-slate-200",
    growth:       "bg-sky-50 text-sky-700 ring-sky-200",
    established:  "bg-primary/10 text-primary ring-primary/20",
    multi_clinic: "bg-amber-50 text-amber-700 ring-amber-200",
};

const SCANNER_LABEL = {
    none: "No scanner", itero_5d: "iTero 5D", trios: "3Shape TRIOS",
    carestream: "Carestream", medit: "Medit", other: "Other",
};


function fmtINR(n) {
    if (!n) return "—";
    if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
    if (n >= 1000)   return `₹${(n / 1000).toFixed(0)}k`;
    return `₹${n}`;
}


export default function SalesPipeline() {
    const navigate = useNavigate();
    const [prospects, setProspects] = useState([]);
    const [reps, setReps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ rep: "", tier: "", scanner: "" });
    const [isManager, setIsManager] = useState(false);

    useEffect(() => {
        let alive = true;
        Promise.all([
            api.get("/sales/prospects"),
            api.get("/sales/reps"),
            api.get("/sales/dashboard"),
        ])
            .then(([p, r, d]) => {
                if (!alive) return;
                setProspects(p.data || []);
                setReps(r.data || []);
                setIsManager(d.data?.is_manager || false);
            })
            .catch(() => null)
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, []);

    const filtered = useMemo(() => {
        return prospects.filter((p) => {
            if (filters.rep && p.assigned_rep_id !== filters.rep) return false;
            if (filters.tier && p.clinic_tier !== filters.tier) return false;
            if (filters.scanner && p.scanner_type !== filters.scanner) return false;
            return true;
        });
    }, [prospects, filters]);

    const byStage = useMemo(() => {
        const m = Object.fromEntries(STAGES.map((s) => [s.id, []]));
        for (const p of filtered) {
            const s = p.pipeline_stage || "prospect";
            (m[s] || (m[s] = [])).push(p);
        }
        return m;
    }, [filtered]);

    const handleDragEnd = async (result) => {
        const { source, destination, draggableId } = result;
        if (!destination) return;
        if (source.droppableId === destination.droppableId && source.index === destination.index) return;

        const fromStage = source.droppableId;
        const toStage = destination.droppableId;

        // Optimistic update
        setProspects((prev) =>
            prev.map((p) => (p.id === draggableId ? { ...p, pipeline_stage: toStage } : p))
        );

        try {
            await api.patch(`/sales/prospects/${draggableId}`, { pipeline_stage: toStage });
            toast.success(`Moved to ${STAGES.find((s) => s.id === toStage)?.label}`);
        } catch (err) {
            // Roll back on failure
            setProspects((prev) =>
                prev.map((p) => (p.id === draggableId ? { ...p, pipeline_stage: fromStage } : p))
            );
            toast.error(err.response?.data?.detail || "Couldn't move the card — try again.");
        }
    };

    if (loading) {
        return <div className="p-8 text-sm text-muted-foreground">Loading pipeline…</div>;
    }

    return (
        <div className="p-4 sm:p-6 space-y-4" data-testid="sales-pipeline">
            {/* Header + filters */}
            <div className="flex items-end justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="font-display text-2xl font-bold tracking-tight">Pipeline</h1>
                    <p className="text-xs text-muted-foreground mt-1">Drag cards between columns to update a doctor&apos;s stage. Updates sync to everyone instantly.</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {isManager && (
                        <FilterSelect
                            value={filters.rep}
                            onChange={(v) => setFilters({ ...filters, rep: v })}
                            placeholder="All reps"
                            options={reps.filter((r) => r.role !== "admin").map((r) => ({ value: r.id, label: r.name || "—" }))}
                            testid="pipeline-filter-rep"
                        />
                    )}
                    <FilterSelect
                        value={filters.tier}
                        onChange={(v) => setFilters({ ...filters, tier: v })}
                        placeholder="All tiers"
                        options={Object.entries(TIER_LABEL).map(([k, v]) => ({ value: k, label: v }))}
                        testid="pipeline-filter-tier"
                    />
                    <FilterSelect
                        value={filters.scanner}
                        onChange={(v) => setFilters({ ...filters, scanner: v })}
                        placeholder="All scanners"
                        options={Object.entries(SCANNER_LABEL).map(([k, v]) => ({ value: k, label: v }))}
                        testid="pipeline-filter-scanner"
                    />
                    {(filters.rep || filters.tier || filters.scanner) && (
                        <button
                            type="button"
                            onClick={() => setFilters({ rep: "", tier: "", scanner: "" })}
                            className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1 px-2 h-7"
                            data-testid="pipeline-clear-filters"
                        >
                            <X size={10} /> Clear
                        </button>
                    )}
                </div>
            </div>

            {/* Kanban board */}
            <DragDropContext onDragEnd={handleDragEnd}>
                <div className="flex gap-3 overflow-x-auto pb-3" data-testid="kanban-board">
                    {STAGES.map((stage) => (
                        <KanbanColumn
                            key={stage.id}
                            stage={stage}
                            cards={byStage[stage.id] || []}
                            onCardClick={(id) => navigate(`/sales/prospects/${id}`)}
                        />
                    ))}
                </div>
            </DragDropContext>
        </div>
    );
}


/* ──────────── Column ──────────── */
function KanbanColumn({ stage, cards, onCardClick }) {
    const totalValue = cards.reduce((sum, c) => sum + (c.estimated_monthly_revenue || 0), 0);
    return (
        <div className="w-[280px] shrink-0 bg-secondary/40 rounded-xl flex flex-col max-h-[calc(100vh-220px)]" data-testid={`kanban-col-${stage.id}`}>
            {/* Column header */}
            <div className="px-3 pt-3 pb-2 sticky top-0 bg-secondary/40 backdrop-blur z-10 rounded-t-xl">
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full" style={{ background: stage.color }} />
                        <span className="text-xs font-bold uppercase tracking-wider">{stage.label}</span>
                    </div>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-white text-foreground font-bold tabular-nums" data-testid={`kanban-count-${stage.id}`}>
                        {cards.length}
                    </span>
                </div>
                {totalValue > 0 && (
                    <div className="text-[10px] text-muted-foreground mt-0.5 tabular-nums">
                        {fmtINR(totalValue)} pipeline value
                    </div>
                )}
            </div>

            {/* Card list — droppable */}
            <Droppable droppableId={stage.id}>
                {(provided, snapshot) => (
                    <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`flex-1 overflow-y-auto px-2 pb-2 space-y-2 transition-colors ${
                            snapshot.isDraggingOver ? "bg-primary/5" : ""
                        }`}
                        style={{ minHeight: 100 }}
                    >
                        {cards.map((card, idx) => (
                            <Draggable key={card.id} draggableId={card.id} index={idx}>
                                {(p, s) => (
                                    <div
                                        ref={p.innerRef}
                                        {...p.draggableProps}
                                        {...p.dragHandleProps}
                                        style={p.draggableProps.style}
                                    >
                                        <KanbanCard
                                            card={card}
                                            dragging={s.isDragging}
                                            onClick={() => onCardClick(card.id)}
                                        />
                                    </div>
                                )}
                            </Draggable>
                        ))}
                        {provided.placeholder}
                        {cards.length === 0 && (
                            <div className="text-[11px] text-muted-foreground text-center py-3 italic">
                                Drop cards here
                            </div>
                        )}
                    </div>
                )}
            </Droppable>
        </div>
    );
}


/* ──────────── Card ──────────── */
function KanbanCard({ card, dragging, onClick }) {
    const tierLabel = TIER_LABEL[card.clinic_tier] || card.clinic_tier;
    const tierTint = TIER_TINT[card.clinic_tier] || TIER_TINT.growth;

    // Stage-specific detail line
    let stageDetail = null;
    const s = card.pipeline_stage;
    if (s === "prospect" || s === "discovery") {
        const scanner = SCANNER_LABEL[card.scanner_type] || "—";
        const provider = card.current_aligner_provider && card.current_aligner_provider !== "none"
            ? `Uses ${card.current_aligner_provider}`
            : "No aligner provider";
        stageDetail = `${scanner} · ${provider}`;
    } else if (s === "demo") {
        stageDetail = card.last_contact_date ? "Demo scheduled / completed" : "Awaiting demo date";
    } else if (s === "trial") {
        const subm = card.pilot_cases_submitted || 0;
        const appr = card.pilot_cases_approved || 0;
        const score = card.pilot_quality_score;
        stageDetail = `${appr}/${subm} pilots approved${score ? ` · ${score}★` : ""}`;
    } else if (s === "negotiation") {
        stageDetail = card.contract_price_per_case
            ? `₹${card.contract_price_per_case.toLocaleString("en-IN")}/case offered`
            : "Pricing in discussion";
    } else if (s === "contract") {
        stageDetail = card.contract_tier
            ? `${TIER_LABEL[card.contract_tier]} tier signed`
            : "Contract signed";
    } else if (s === "active") {
        const thisMo = card.cases_submitted_this_month || 0;
        const lastMo = card.cases_submitted_last_month || 0;
        const trend = thisMo > lastMo ? "↑" : thisMo < lastMo ? "↓" : "→";
        stageDetail = `${thisMo} cases this month ${trend} (last: ${lastMo})`;
    }

    return (
        <button
            type="button"
            onClick={onClick}
            className={`w-full text-left bg-white rounded-lg border ${dragging ? "border-primary shadow-lg ring-2 ring-primary/30" : "border-border"} p-3 hover:border-primary/40 hover:shadow-sm transition-all cursor-grab active:cursor-grabbing`}
            data-testid={`kanban-card-${card.id}`}
        >
            {/* Clinic + tier */}
            <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-bold leading-tight truncate">{card.clinic_name}</div>
                    <div className="text-[11px] text-muted-foreground truncate inline-flex items-center gap-1 mt-0.5">
                        <MapPin size={10} /> {card.doctor_name}
                    </div>
                </div>
                <span className={`shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ring-1 ${tierTint}`}>
                    {tierLabel}
                </span>
            </div>

            {/* City */}
            {card.city && (
                <div className="text-[10px] text-muted-foreground mb-1.5 truncate">
                    {card.city}{card.state ? `, ${card.state}` : ""}
                </div>
            )}

            {/* Stage detail line */}
            {stageDetail && (
                <div className="text-[10px] text-foreground/80 bg-secondary/40 rounded px-2 py-1 mb-2 truncate">
                    {stageDetail}
                </div>
            )}

            {/* Footer — est cases + value */}
            <div className="flex items-center justify-between gap-2 pt-1.5 border-t border-border/40">
                <div className="text-[10px] text-muted-foreground inline-flex items-center gap-1">
                    <Stack size={10} weight="duotone" />
                    <span className="tabular-nums font-semibold">{card.estimated_monthly_cases}</span> cases/mo
                </div>
                <div className="text-[10px] text-primary font-bold tabular-nums">
                    {fmtINR(card.estimated_monthly_revenue)}
                </div>
            </div>
        </button>
    );
}


/* ──────────── FilterSelect — tiny native select ──────────── */
function FilterSelect({ value, onChange, placeholder, options, testid }) {
    return (
        <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="h-7 px-2 text-[11px] rounded-md border border-border bg-white outline-none focus:ring-2 focus:ring-primary/30"
            data-testid={testid}
        >
            <option value="">{placeholder}</option>
            {options.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
            ))}
        </select>
    );
}
